"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.propTypes = exports.defaultProps = exports.default = exports.PromoCardCompact = void 0;
require("./PromoCardCompact.css");
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _conditionalAttributes = require("../../util/conditionalAttributes/conditionalAttributes");
var _Card = _interopRequireDefault(require("../Card/Card"));
var _LoaderGhostCard = _interopRequireDefault(require("../LoaderGhostCard/LoaderGhostCard"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * PromoCardCompact component
 *
 * @method
 * @param { PromoCardCompactProps } props
 * @returns PromoCardCompact
 */

const PromoCardCompact = props => {
  const {
    backgroundColor,
    body1,
    body2,
    borderColor = defaultProps.borderColor,
    disclaimer,
    elevation = defaultProps.elevation,
    externalActionElement,
    externalFullyClickableActionElement,
    externalMediaElement,
    fullyClickableParent,
    headline,
    headlineTag,
    tags,
    textColor = defaultProps.textColor,
    isLoading = defaultProps.isLoading,
    loaderHeight
  } = props;
  const isKnockOut = textColor === 'content-knockout';
  const componentClassName = (0, _classnames.default)('pal-c-PromoCardCompact', {
    'pal-c-PromoCardCompact--borderColor': borderColor,
    [`pal-c-PromoCardCompact__elevation--${elevation}`]: elevation,
    'pal-c-PromoCardCompact--isKnockOut': isKnockOut,
    'pal-c-PromoCardCompact--isLoading': isLoading
  });
  const componentStyle = {
    ...(borderColor && {
      '--borderColor': `var( --pal-colors-default-${borderColor} )`
    }),
    ...(textColor && {
      '--textColor': `var( --pal-colors-default-${textColor} )`
    }),
    ...(0, _conditionalAttributes.conditionalAttributes)(isLoading ? {
      '--pal-loaderghost-card-height': loaderHeight
    } : null)
  };
  return /*#__PURE__*/_react.default.createElement("div", {
    className: componentClassName,
    style: componentStyle
  }, /*#__PURE__*/_react.default.createElement(_Card.default, {
    backgroundColor: isLoading ? 'background-xsubtle' : backgroundColor,
    externalMedia: externalMediaElement,
    externalAction: externalActionElement,
    imgGap: "md",
    contentGap: "md",
    title: {
      text: headline,
      tagName: headlineTag,
      textStyle: 'title-xs'
    },
    description: {
      text: body1,
      textStyle: 'body-sm'
    },
    subDescriptionOne: {
      text: body2,
      textStyle: 'body-sm'
    },
    subDescriptionTwo: {
      text: disclaimer,
      textStyle: 'body-sm'
    },
    tags: tags,
    fullyClickableParent: fullyClickableParent,
    externalActionFullyClick: externalFullyClickableActionElement,
    isLoading: isLoading
  }));
};

/**
 * Property type definitions
 * @typedef PromoCardCompactProps
 * @type {object}
 * @property {string} backgroundColor - Sets the background color of the card
 * @property {string} borderColor - Sets the border color of the card
 * @property {string} body1 - Sets the body copy 1 text
 * @property {string} body2 - Sets the body copy 2 text
 * @property {string} disclaimer - Sets the disclaimer text of the card
 * @property {string} elevation - Sets the elevation of the card
 * @property {object} externalActionElement - Accepts actions to render from web-site with business logic
 * @property {object} externalMediaElement - Accepts UltaAsset to render the image for the Card
 * @property {object} externalFullyClickableActionElement - Accepts fully clickable absolute action from web-site
 * @property {boolean} fullyClickableParent - Set to true if the card can be fully clickable
 * @property {string} headline - Sets the headline text for the card
 * @property {string} headlineTag - Sets the headline tag for the card headline
 * @property {array} tags - Accepts array of tag objects to render on the card body
 * @property {string} textColor - Sets the color of the card content
 * @property {boolean} isLoading - sets the loading state for the banner,
 * @property {string} loaderHeight - Accepts the height of the loader( e.g. "13rem" )
 */
exports.PromoCardCompact = PromoCardCompact;
const propTypes = exports.propTypes = {
  /**
   * Sets the background color of the card
   */
  backgroundColor: _propTypes.default.string,
  /**
   * Sets the border color of the card
   */
  borderColor: _propTypes.default.string,
  /**
   * Sets the body copy 1 text
   */
  body1: _propTypes.default.string,
  /**
   * Sets the body copy 2 text
   */
  body2: _propTypes.default.string,
  /**
   * Sets the disclaimer text of the card
   */
  disclaimer: _propTypes.default.string,
  /**
   * Sets the elevation of the card
   */
  elevation: _propTypes.default.oneOf(['low', 'medium', 'high', 'top']),
  /**
   * Accepts actions to render from web-site with business logic
   */
  externalActionElement: _propTypes.default.object,
  /**
   * Accepts UltaAsset to render the image for the Card
   */
  externalMediaElement: _propTypes.default.object,
  /**
   * Accepts fully clickable absolute action from web-site
   */
  externalFullyClickableActionElement: _propTypes.default.object,
  /**
   * Set to true if the card can be fully clickable
   */
  fullyClickableParent: _propTypes.default.bool,
  /**
   * Sets the headline text for the card
   */
  headline: _propTypes.default.string,
  /**
   * Sets the headline tag for the card headline
   */
  headlineTag: _propTypes.default.string,
  /**
   * Accepts array of tag objects to render on the card body
   */
  tags: _propTypes.default.array,
  /**
   * Sets the color of the card content
   */
  textColor: _propTypes.default.oneOf(['content-default', 'content-knockout']),
  /**
     * Sets the loading state of the banner
     */
  isLoading: _propTypes.default.bool,
  /**
     * Accepts the height of the loader.
     * Expect a CSS length string (for example: **"13rem"**)
     */
  loaderHeight: _propTypes.default.string
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} borderColor=undefined - Sets default border color to undefined
 * @property {string} elevation=undefined - Sets default elevation to undefined
 * @property {string} textColor='content-default' - Sets default textColor to content-default
 * @property {boolean} isLoading - isLoading: false - sets the default loading state to false
 */
const defaultProps = exports.defaultProps = {
  borderColor: undefined,
  elevation: undefined,
  textColor: 'content-default',
  isLoading: false
};
PromoCardCompact.propTypes = propTypes;
var _default = exports.default = PromoCardCompact;